import classNames from "classnames";
import { useFeature } from "flagged";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Column } from "react-table";

import style from "./delivery-history.scss";
import DeliveryHistoryMenuItems from "./DeliveryHistoryMenuItems";
import { fetchRateVersionNameFromUuid, ViewDeliveryHistory } from "./ViewDeliveryHistory";
import KebabMenu from "components/kebab-menu/KebabMenu";
import { getDeliveryTypes } from "components/licenses/common";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import SearchView from "components/search/SearchView";
import StatusBadge, { Status } from "components/status-badge/StatusBadge";
import DateCell from "components/table/DateCell";
import Table, { deriveColumnWidth } from "components/table/Table";
import TextWithTooltip from "components/table/TextWithTooltip";
import { DeliveryHistory, DeliveryStatus, LicenseDeliveryHistory } from "domain/licenses";
import { CONTRACT_TYPE_TRIAL } from "domain/tenants";
import { FLAG_SCHEDULED_LICENSE_DELIVERY } from "services/feature/FeatureFlagService";
import {
    extractTenantLicenseRateUuid,
    fetchLicenseRateVersion,
    licenseService,
    ProductToRateList,
} from "services/licenses/LicenseService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import {
    getCurrentTenantDetails,
    hasTenantCookie,
    isLicensingModelBundleWithToken,
    isUserParentInternal,
} from "services/tenants/tenantCookieService";
import buttonStyle from "styles/buttons.scss";
import formStyle from "styles/form.scss";
import layoutStyle from "styles/layout.scss";
import { RepositoryKey } from "utils/repository";

import testIds from "testIds.json";

interface TableState {
    deliveryHistory: DeliveryHistory[];
    count: number;
    cursor: string;
    scrollPosition: number;
}

interface Props {
    initialDeliveryHistory?: DeliveryHistory[];
    requestFailureMessage?: string;
    onUpdate: (isSync: boolean) => void;
    count: number;
}

const DeliveryHistoryTable = (rawProps: Props): JSX.Element => {
    const props = { initialDeliveryHistory: [], requestFailureMessage: "", ...rawProps };
    const { t } = useTranslation();
    const isScheduledLicenseDeliveryFlagEnabled = useFeature(FLAG_SCHEDULED_LICENSE_DELIVERY);
    const [failureMessage, setFailureMessage] = React.useState<string>("");
    const [deliveryHistoryVisibility, setDeliveryHistoryVisibility] = React.useState(false);
    const [initialLoadingForDeliveryDetails, setInitialLoadingForDeliveryDetails] = React.useState<boolean>(true);
    const tableContainerRef = React.useRef<HTMLDivElement>(null);
    const [search, setSearchQuery] = React.useState("");
    const [statusFilter, setStatusFilter] = React.useState<DeliveryStatus>(DeliveryStatus.ALL);
    const [tokenRateVersions, setTokenRateVersions] = React.useState<ProductToRateList[]>([]);
    const [rateUuid, setTokenRateUUid] = React.useState<string>("");
    const DELIVERY_TYPES = getDeliveryTypes();

    const DEFAULT_DELIVERY_DETAILS = {
        type: "",
        opportunityId: "",
        notes: "",
        caseNumber: "",
        licenses: [],
        uuid: "",
        creatorUuid: "",
        creationDate: "",
        editorUuid: "",
        editDate: "",
    };
    const [activeDeliveryHistoryDetails, setActiveDeliveryHistoryDetails] =
        React.useState<LicenseDeliveryHistory>(DEFAULT_DELIVERY_DETAILS);

    const fetchDeliveryHistoryDetails = (uuid: string) => {
        setInitialLoadingForDeliveryDetails(true);
        setActiveDeliveryHistoryDetails(DEFAULT_DELIVERY_DETAILS);
        setFailureMessage("");
        setDeliveryHistoryVisibility(true);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const fetchLicenseDeliveries = licenseService.fetchLicenseDeliveryHistory(uuid, abortController);
        Promise.all([fetchLicenseDeliveries, tokenRateVersions])
            .then(([fetchedLicenseDeliveries, fetchedRateVersions]) => {
                fetchedLicenseDeliveries.licenseRateVersion = fetchRateVersionNameFromUuid(
                    fetchedLicenseDeliveries.licenseRateVersion,
                    fetchedRateVersions
                );
                setActiveDeliveryHistoryDetails(fetchedLicenseDeliveries);
                setInitialLoadingForDeliveryDetails(fetchedLicenseDeliveries.type == "");
            })
            .catch(() => {
                setInitialLoadingForDeliveryDetails(true);
                setFailureMessage(t("DeliveryHistory.deliveryHistoryDialog.failedToLoadLicenseDetails"));
            });
    };

    const columns: Array<Column<DeliveryHistory>> = [
        {
            Header: () => <TextWithTooltip text={t("Common.creationDate")} key="creationDate" />,
            accessor: "creationDate",
            Cell: (cellInfo) => (
                <>
                    {/* As kebab menu has only one conditional edit action then no need to display empty kebab menu  */}
                    {hasTenantCookie() && cellInfo.cell.row.original.deliveryStatus !== DeliveryStatus.DELETED ? (
                        <KebabMenu>
                            <DeliveryHistoryMenuItems
                                deliveryHistoryId={cellInfo.cell.row.original.uuid}
                                onDeliveryHistoryEdit={props.onUpdate}
                                tokenRateVersions={tokenRateVersions}
                                rateUuid={rateUuid}
                                deliveryStatus={cellInfo.cell.row.original.deliveryStatus}
                            />
                        </KebabMenu>
                    ) : (
                        ""
                    )}
                    <button
                        className={style.dateCell}
                        onClick={() => {
                            fetchDeliveryHistoryDetails(cellInfo.cell.row.original.uuid);
                            usageStatisticsService.sendEvent({
                                category: Category.LICENSE_DELIVERY,
                                action: Action.VIEW_LICENSE_DELIVERY,
                            });
                        }}
                    >
                        <DateCell tooltip={true} value={cellInfo.cell.row.original.creationDate} />
                    </button>
                </>
            ),
            width: deriveColumnWidth(20, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("DeliveryHistory.deliveryHistoryTable.deliveryType")} key="type" />,
            accessor: "type",
            Cell: ({ cell: { value } }) => (
                <TextWithTooltip
                    text={
                        value === CONTRACT_TYPE_TRIAL
                            ? t("DeliveryHistory.deliveryType.trial")
                            : DELIVERY_TYPES.get(value)
                    }
                />
            ),
            width: deriveColumnWidth(isScheduledLicenseDeliveryFlagEnabled ? 15 : 20, tableContainerRef),
        },
        ...(isScheduledLicenseDeliveryFlagEnabled
            ? [
                  {
                      Header: () => (
                          <TextWithTooltip
                              text={t("DeliveryHistory.deliveryHistoryTable.deliveryStatus")}
                              key="deliveryStatus"
                          />
                      ),
                      accessor: "deliveryStatus" as const,
                      Cell: ({ cell: { value } }: { cell: { value?: string } }) => {
                          const statusMap = new Map<string, { status: Status; title: string }>([
                              [
                                  DeliveryStatus.SCHEDULED,
                                  {
                                      status: Status.INFO,
                                      title: t("DeliveryHistory.deliveryHistoryTable.statusFilter.scheduled"),
                                  },
                              ],
                              [
                                  DeliveryStatus.DELIVERED,
                                  {
                                      status: Status.SUCCESS,
                                      title: t("DeliveryHistory.deliveryHistoryTable.statusFilter.delivered"),
                                  },
                              ],
                              [
                                  DeliveryStatus.TRIGGERED_MANUALLY,
                                  {
                                      status: Status.SUCCESS,
                                      title: t("DeliveryHistory.deliveryHistoryTable.statusFilter.triggered_manually"),
                                  },
                              ],
                              [
                                  DeliveryStatus.DELETED,
                                  {
                                      status: Status.ERROR,
                                      title: t("DeliveryHistory.deliveryHistoryTable.statusFilter.deleted"),
                                  },
                              ],
                              [
                                  DeliveryStatus.ON_HOLD,
                                  {
                                      status: Status.WARNING,
                                      title: t("DeliveryHistory.deliveryHistoryTable.statusFilter.onHold"),
                                  },
                              ],
                              [
                                  DeliveryStatus.FAILED,
                                  {
                                      status: Status.ERROR,
                                      title: t("DeliveryHistory.deliveryHistoryTable.statusFilter.failed"),
                                  },
                              ],
                          ]);

                          if (!value) {
                              const deliveredConfig = statusMap.get(DeliveryStatus.DELIVERED);
                              if (deliveredConfig) {
                                  return <StatusBadge values={[deliveredConfig]} tooltip={true} />;
                              }
                              return <TextWithTooltip text="" />;
                          }

                          const statusConfig = statusMap.get(value);
                          if (statusConfig) {
                              return <StatusBadge values={[statusConfig]} tooltip={true} />;
                          }

                          return <TextWithTooltip text={value} />;
                      },
                      width: deriveColumnWidth(15, tableContainerRef),
                  },
              ]
            : []),
        ...(isScheduledLicenseDeliveryFlagEnabled && isUserParentInternal()
            ? [
                  {
                      Header: () => (
                          <TextWithTooltip
                              text={t("DeliveryHistory.deliveryHistoryTable.deliverySchedule")}
                              key="deliverySchedule"
                          />
                      ),
                      accessor: "deliverySchedule" as const,
                      Cell: ({ cell: { value } }: { cell: { value?: string | null } }) =>
                          value && value.trim() !== "" ? (
                              <DateCell tooltip={true} value={value} />
                          ) : (
                              <TextWithTooltip content="-" text="" />
                          ),
                      width: deriveColumnWidth(15, tableContainerRef),
                  },
                  {
                      Header: () => (
                          <TextWithTooltip
                              text={t("DeliveryHistory.deliveryHistoryTable.editor")}
                              key="editorUsername"
                          />
                      ),
                      accessor: "editorUsername" as const,
                      Cell: ({ cell: { value } }: { cell: { value?: string | null } }) => {
                          const displayValue = value && value.trim() !== "" ? value : "-";
                          return <TextWithTooltip content={displayValue} text="" />;
                      },
                      width: deriveColumnWidth(15, tableContainerRef),
                  },
                  {
                      Header: () => (
                          <TextWithTooltip text={t("DeliveryHistory.deliveryHistoryTable.editDate")} key="editDate" />
                      ),
                      accessor: "editDate" as const,
                      Cell: ({ cell: { value } }: { cell: { value?: string | null } }) =>
                          value && value.trim() !== "" ? (
                              <DateCell tooltip={true} value={value} />
                          ) : (
                              <TextWithTooltip content="-" text="" />
                          ),
                      width: deriveColumnWidth(15, tableContainerRef),
                  },
              ]
            : []),
        {
            Header: () => <TextWithTooltip text={t("DeliveryHistory.deliveryHistoryTable.notes")} key="notes" />,
            accessor: "notes",
            Cell: ({ cell: { value } }) => <TextWithTooltip text={value} />,
            width: deriveColumnWidth(
                isScheduledLicenseDeliveryFlagEnabled ? (isUserParentInternal() ? 28 : 38) : 48,
                tableContainerRef
            ),
        },
    ];
    const [setState, setDeliveryHistoryData] = React.useState<TableState>({
        deliveryHistory: [],
        count: 0,
        cursor: "",
        scrollPosition: 0,
    });
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>(props.requestFailureMessage);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [initialLoading, setInitialLoading] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);

    const fetchData = (initialLoading: boolean) => {
        setInitialLoading(initialLoading);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const statusParam = statusFilter === DeliveryStatus.ALL ? "all" : statusFilter.toLowerCase();
        licenseService
            .fetchDeliveryHistory(abortController, search, initialLoading ? "" : setState.cursor, statusParam)
            .then((data) => {
                let filteredHistories = data.deliveryHistories;
                if (!hasTenantCookie()) {
                    filteredHistories = data.deliveryHistories.filter(
                        (item) => item.deliveryStatus !== DeliveryStatus.DELETED
                    );
                }
                setDeliveryHistoryData((prevState) => ({
                    ...prevState,
                    scrollPosition: prevState.deliveryHistory.length - 1,
                    deliveryHistory: initialLoading
                        ? filteredHistories
                        : prevState.deliveryHistory.concat(filteredHistories),
                    count: filteredHistories.length,
                    cursor: data.cursor,
                }));
                setLoading(false);
                setRequestFailureMessage("");
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("DeliveryHistory.deliveryHistoryTable.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setInitialLoading(false);
                    setLoading(false);
                }
            });
    };

    React.useEffect(() => {
        if (statusFilter === DeliveryStatus.DELETED && !hasTenantCookie()) {
            setStatusFilter(DeliveryStatus.ALL);
        }
    }, [statusFilter]);

    React.useEffect(() => {
        if (props.initialDeliveryHistory.length > 0) {
            return;
        }
        setDeliveryHistoryData({ deliveryHistory: [], count: 0, cursor: "", scrollPosition: 0 });
        fetchData(true);
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, [props.count, search, statusFilter]);

    async function fetchRatesFromApi() {
        const rates = isLicensingModelBundleWithToken() ? await fetchLicenseRateVersion() : [];
        setTokenRateVersions(rates);
        setTokenRateUUid(extractTenantLicenseRateUuid(rates, getCurrentTenantDetails().type));
    }

    React.useEffect(() => {
        if (isLicensingModelBundleWithToken()) {
            fetchRatesFromApi();
        }
    }, []);

    const modal = (
        <Modal
            key={1}
            isOpen={deliveryHistoryVisibility}
            hideModal={() => setDeliveryHistoryVisibility(false)}
            modalTitle={t("DeliveryHistory.deliveryHistoryDialog.title")}
        >
            {failureMessage != "" ? (
                failureMessage
            ) : (
                <ViewDeliveryHistory
                    deliveryHistoryDetails={activeDeliveryHistoryDetails}
                    loading={initialLoadingForDeliveryDetails}
                    tenantTier={getCurrentTenantDetails().tenantTier}
                    tenantType={getCurrentTenantDetails().type}
                />
            )}
        </Modal>
    );

    const allFilterOptions = [
        DeliveryStatus.ALL,
        DeliveryStatus.SCHEDULED,
        DeliveryStatus.DELIVERED,
        DeliveryStatus.DELETED,
        DeliveryStatus.ON_HOLD,
        DeliveryStatus.FAILED,
    ];

    const filterOptions = hasTenantCookie()
        ? allFilterOptions
        : allFilterOptions.filter((filter) => filter !== DeliveryStatus.DELETED);

    const filterToTranslation = new Map<DeliveryStatus, string>([
        [DeliveryStatus.ALL, t("DeliveryHistory.deliveryHistoryTable.statusFilter.all")],
        [DeliveryStatus.SCHEDULED, t("DeliveryHistory.deliveryHistoryTable.statusFilter.scheduled")],
        [DeliveryStatus.DELIVERED, t("DeliveryHistory.deliveryHistoryTable.statusFilter.delivered")],
        [DeliveryStatus.TRIGGERED_MANUALLY, t("DeliveryHistory.deliveryHistoryTable.statusFilter.triggered_manually")],
        [DeliveryStatus.DELETED, t("DeliveryHistory.deliveryHistoryTable.statusFilter.deleted")],
        [DeliveryStatus.ON_HOLD, t("DeliveryHistory.deliveryHistoryTable.statusFilter.onHold")],
        [DeliveryStatus.FAILED, t("DeliveryHistory.deliveryHistoryTable.statusFilter.failed")],
    ]);

    let dataCount = null;
    if (setState.deliveryHistory.length > 0) {
        dataCount = t("Common.defaultSearchResultHint", { dataCount: setState.deliveryHistory.length });
    }
    return (
        <>
            <div className={layoutStyle.aboveTable}>
                <div className={classNames(layoutStyle.recordCount, style.quickFilterContainer)}>
                    {isScheduledLicenseDeliveryFlagEnabled && (
                        <div data-testid={testIds.common.primaryView.table.filter.itself}>
                            {filterOptions.map((filter) => (
                                <button
                                    onClick={() => {
                                        setStatusFilter(filter);
                                    }}
                                    key={filter}
                                    className={classNames(style.quickFilterButton, {
                                        [style.active]: statusFilter === filter,
                                    })}
                                    data-testid={testIds.common.primaryView.table.filter.button}
                                    data-type={filter}
                                >
                                    {filterToTranslation.get(filter)}
                                </button>
                            ))}
                        </div>
                    )}
                    <div>{dataCount}</div>
                </div>
                <div className={formStyle.search}>
                    <SearchView setSearch={setSearchQuery} searchInProgress={false} />
                </div>
            </div>
            <div className={layoutStyle.tableWrapper} ref={tableContainerRef}>
                <Table
                    tableIdentity={RepositoryKey.DELIVERY_HISTORY_TABLE}
                    data={setState.deliveryHistory}
                    columns={columns}
                    loaded={!initialLoading}
                    loading={loading}
                    failureMessage={requestFailureMessage}
                    tooltips={true}
                    emptyMessage={t("DeliveryHistory.deliveryHistoryTable.emptyStateMessage")}
                />
            </div>
            {setState.cursor != null &&
                setState.cursor != "" &&
                setState.deliveryHistory.length != 0 &&
                requestFailureMessage === "" &&
                (loading ? (
                    <LoadingIndicator small={true} />
                ) : (
                    <button
                        className={buttonStyle.loadMoreButtonWithoutIcon}
                        onClick={() => {
                            fetchData(false);
                            usageStatisticsService.sendEvent({
                                label: "delivery-history",
                                action: Action.LOAD_MORE,
                                category: Category.WORKFLOW,
                            });
                        }}
                        data-testid={testIds.common.primaryView.table.loadMoreButton}
                    >
                        {t("Common.loadMore")}
                    </button>
                ))}
            {modal}
        </>
    );
};

export default DeliveryHistoryTable;
